import Axios, { AxiosPromise, AxiosResponse } from 'axios'

export function isInertiaResponse(response: AxiosResponse): boolean {
  if (response && response.headers) {
    return response.headers['x-inertia'] === 'true'
  }

  return false
}

export function getInertiaRequestId(): string {
  return Math.random().toString(36).substring(7)
}

export function reloadInertiaProp(component: string, only: string): AxiosPromise<any> {
  // https://stackoverflow.com/questions/27513994/chrome-stalls-when-making-multiple-requests-to-same-resource
  const pageUrl = new URL(window.location.href)
  pageUrl.searchParams.set('_inertia', getInertiaRequestId())
  return Axios({
    method: 'GET',
    url: pageUrl.toString(),
    headers: {
      'Accept': 'text/html, application/xhtml+xml',
      'X-Requested-With': 'XMLHttpRequest',
      'X-Inertia': 'true',
      ...(only.length
        ? {
            'X-Inertia-Partial-Component': component,
            'X-Inertia-Partial-Data': only,
          }
        : {}),
      //...(this.page.version ? { 'X-Inertia-Version': this.page.version } : {}),
    },
  }).then((response) => {
    if (!isInertiaResponse(response)) {
      return Promise.reject({ response })
    }
    return response
  })
}

// reloadInertiaPropsBulk can reload many props in bulk without one cancelling others.
// This makes a single request for all props in `only`
export function reloadInertiaPropsBulk(component: string, only: string[]): Promise<any> {
  return reloadInertiaProp(component, only.join(',')).then((response) => {
    return response.data.props
  })
}

// Checks if a given app version can be considered 'current' or not. This is used to trigger reload-when-stale
// logic. A given version will be considered 'stale' only if the version has changed since initial page load
// and has been consistently seen for at least 5 minutes (to prevent reload loops during deployments).
const minutesUntilStale = 5
export function isCurrentAppVersion(newVersion: string) {
  // If we don't have an appVersion recorded, such as after a fresh page load, consider it the 'current' version.
  // NOTE: window.appVersion is cleared on new tabs and all page reloads
  if (!window.appVersion) {
    window.appVersion = { version: newVersion, firstSeenAt: new Date(), previousVersion: newVersion }
    return true
  }

  const { version, firstSeenAt, previousVersion } = window.appVersion
  // If the version has changed, still consider it 'current' until enough time has also elapsed.
  if (newVersion !== version) {
    window.appVersion = { version: newVersion, firstSeenAt: new Date(), previousVersion: version }
    return true
  }

  // If the newVersion matches previousVersion, then it hasn't changed since page load or it briefly toggled
  // during a deploy. Still consider it current.
  if (newVersion === previousVersion) {
    return true
  }

  // At this point, we know the version has changed since initial page load. Check that enough time has passed since
  // we begain consistently seeing this version. This prevents edge cases where users attempt to navigate during a
  // deploy and get stuck in a page reload loop.
  const timeDiffMinutes = (new Date().getTime() - firstSeenAt.getTime()) / (1000 * 60)
  return timeDiffMinutes < minutesUntilStale
}
